// The package root is browser-safe by contract (browser-safe.test.ts guards it):
// computeVersionHash → node:crypto lives on the `@fixit/bozp-content/version-hash`
// subpath, never on the barrel, so this value import is fine in the player bundle.
import {
  ANIMATION_SCENE_SPEC,
  type AnimationBlock,
  type AnimationScene,
  type LocaleCode,
} from '@fixit/bozp-content';
import { type JSX, useEffect, useState } from 'react';
import { resolveLocalized } from '../../locale';
import { SCENE_CSS } from './css';
import { BitumenTorchScene, PressureReliefScene, RefrigerantLeakScene } from './scenes-cert';
import { CprRhythmScene, ExtinguisherMatchScene, FireTriangleScene } from './scenes-core';
import {
  AsbestosStopScene,
  ChainsawKickbackScene,
  CheckInLoopScene,
  LadderContactScene,
  SiteInductionScene,
  ToolCheckScene,
} from './scenes-market';
import {
  AtmosphereTestScene,
  CargoShiftScene,
  ChemicalBottlesScene,
  FireWatchScene,
  GasLeakScene,
  LadderAngleScene,
  LiftTechniqueScene,
  LockoutTagoutScene,
  PpeMatchScene,
} from './scenes-trades';

export const SCENES: Record<AnimationScene, (props: { en: boolean }) => JSX.Element> = {
  fireTriangle: FireTriangleScene,
  extinguisherMatch: ExtinguisherMatchScene,
  cprRhythm: CprRhythmScene,
  ppeMatch: PpeMatchScene,
  ladderAngle: LadderAngleScene,
  lockoutTagout: LockoutTagoutScene,
  gasLeak: GasLeakScene,
  atmosphereTest: AtmosphereTestScene,
  chemicalBottles: ChemicalBottlesScene,
  liftTechnique: LiftTechniqueScene,
  fireWatch: FireWatchScene,
  cargoShift: CargoShiftScene,
  siteInduction: SiteInductionScene,
  toolCheck: ToolCheckScene,
  ladderContact: LadderContactScene,
  chainsawKickback: ChainsawKickbackScene,
  asbestosStop: AsbestosStopScene,
  checkInLoop: CheckInLoopScene,
  refrigerantLeak: RefrigerantLeakScene,
  bitumenTorch: BitumenTorchScene,
  pressureRelief: PressureReliefScene,
};

/** Auto-advance cadence while playing — one guided step every three seconds. */
export const STEP_AUTO_ADVANCE_MS = 3000;

type FrameUi = {
  prev: string;
  next: string;
  play: string;
  pause: string;
  steps: string;
  controls: string;
  counter: (n: number, of: number) => string;
};

/** Frame chrome labels (renderer-owned UI text — the instructional voice lives in the content block). */
const FRAME_UI = {
  'cs-CZ': {
    prev: 'Předchozí krok',
    next: 'Další krok',
    play: 'Přehrát',
    pause: 'Pozastavit',
    steps: 'Kroky',
    controls: 'Ovládání animace',
    counter: (n, of) => `Krok ${n} z ${of}`,
  },
  'en-US': {
    prev: 'Previous step',
    next: 'Next step',
    play: 'Play',
    pause: 'Pause',
    steps: 'Steps',
    controls: 'Animation controls',
    counter: (n, of) => `Step ${n} of ${of}`,
  },
  'sk-SK': {
    prev: 'Predchádzajúci krok',
    next: 'Ďalší krok',
    play: 'Prehrať',
    pause: 'Pozastaviť',
    steps: 'Kroky',
    controls: 'Ovládanie animácie',
    counter: (n, of) => `Krok ${n} z ${of}`,
  },
  'uk-UA': {
    prev: 'Попередній крок',
    next: 'Наступний крок',
    play: 'Відтворити',
    pause: 'Пауза',
    steps: 'Кроки',
    controls: 'Керування анімацією',
    counter: (n, of) => `Крок ${n} з ${of}`,
  },
} satisfies Record<LocaleCode, FrameUi>;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const ICON = {
  prev: <path d="M9 1 3 6l6 5z" />,
  next: <path d="m3 1 6 5-6 5z" />,
  play: <path d="M2 1v10l9-5z" />,
  pause: <path d="M2 1h3v10H2zm5 0h3v10H7z" />,
};

function Glyph({ name }: { name: keyof typeof ICON }) {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      {ICON[name]}
    </svg>
  );
}

/**
 * Guided-steps frame around a self-contained SVG scene (zero remote assets —
 * unlike image/hotspotImage there is nothing to 404). The scene's step-bound
 * groups show off `data-step`, so every step is a complete static frame; the
 * frame auto-advances while playing, starts paused under
 * `prefers-reduced-motion`, and exposes prev/next/play-pause plus a step rail
 * built from the content block's authored `steps` (label + instruction).
 * Ambient one-step scenes get only play/pause. In-scene micro-labels are still
 * renderer-owned (cs default, en-US variant); the authored voice lives in
 * `steps[].say` and the optional `caption`.
 */
export function AnimationBlockView({
  block,
  locale,
}: {
  block: AnimationBlock;
  locale: LocaleCode;
}) {
  const Scene = SCENES[block.scene];
  const stepCount = ANIMATION_SCENE_SPEC[block.scene].steps;
  const steps = block.steps ?? [];
  const ui = FRAME_UI[locale];
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    if (!playing || stepCount < 2) return;
    const id = window.setInterval(
      () => setStep((current) => (current + 1) % stepCount),
      STEP_AUTO_ADVANCE_MS,
    );
    return () => window.clearInterval(id);
  }, [playing, stepCount]);

  const goTo = (next: number) => {
    setStep(((next % stepCount) + stepCount) % stepCount);
    setPlaying(false);
  };
  const say = steps[step]?.say;

  return (
    <figure
      data-block-type="animation"
      data-scene={block.scene}
      data-step={step + 1}
      data-playing={playing ? 'true' : 'false'}
    >
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: static module-local CSS constant, no user input */}
      <style dangerouslySetInnerHTML={{ __html: SCENE_CSS }} />
      {/* keyed by step so the verdict badge re-pops on every step change */}
      <div className="hbA-stage" key={step}>
        <Scene en={locale === 'en-US'} />
      </div>
      {say && (
        <p className="hbA-say" aria-live="polite">
          {resolveLocalized(say, locale)}
        </p>
      )}
      {stepCount > 1 && steps.length > 0 && (
        <ol className="hbA-rail" aria-label={ui.steps}>
          {steps.map((s, i) => (
            <li key={s.label['cs-CZ']}>
              <button
                type="button"
                className="hbA-pill"
                aria-current={i === step ? 'step' : undefined}
                data-done={i < step ? 'true' : 'false'}
                onClick={() => goTo(i)}
              >
                <span className="hbA-pill-n" aria-hidden="true">
                  {i + 1}
                </span>
                {resolveLocalized(s.label, locale)}
              </button>
            </li>
          ))}
        </ol>
      )}
      <fieldset className="hbA-controls" aria-label={ui.controls}>
        {stepCount > 1 && (
          <button type="button" aria-label={ui.prev} onClick={() => goTo(step - 1)}>
            <Glyph name="prev" />
          </button>
        )}
        <button
          type="button"
          aria-label={playing ? ui.pause : ui.play}
          aria-pressed={playing}
          onClick={() => setPlaying((current) => !current)}
        >
          <Glyph name={playing ? 'pause' : 'play'} />
        </button>
        {stepCount > 1 && (
          <button type="button" aria-label={ui.next} onClick={() => goTo(step + 1)}>
            <Glyph name="next" />
          </button>
        )}
        {stepCount > 1 && <span className="hbA-counter">{ui.counter(step + 1, stepCount)}</span>}
      </fieldset>
      {block.caption && <figcaption>{resolveLocalized(block.caption, locale)}</figcaption>}
    </figure>
  );
}
